import {
    ActionFunctionArgs,
    Form,
    LoaderFunctionArgs,
    redirect, useFetcher,
    useLoaderData, useNavigate,
} from "react-router-dom";
import toast from "react-hot-toast";
import IndeterminateCheckbox from "../../../components/inputs/IndeterminantCheckbox";
import {Input} from "../../../components/inputs/Input";
import {createApolloClient} from "../../../providers/ApolloClientFactory";
import {Categories, Item} from "../../../types/item";
import {UPDATE_ORDER_AHEAD_LOCATION} from "./mutations";
import {useEffect, useRef, useState} from "react";
import {userStore} from "../../../store/user";
import CustomSelect from "../../../components/customSelect";
import {
    GET_LIST_EVENTS_BY_ORGANIZATION, GET_LIST_PAYMENT_PROCESSORS_BY_ORGANIZATION, GET_LIST_VENDORS_BY_ORGANIZATION,
} from "../../../queries";
import {organizationStore} from "../../../store/organization";
import {Event} from "../../../types/event";
import {SecondaryButton, TertiaryButton} from "../../../components/Button";
import DeleteModal from "../../../components/DeleteModal";
import {usePermissions} from "../../auth/api";
import { canManageOrderAhead } from "../../order-ahead/access";
import TicketingItemHTML from "../components/TickingItemHTML";
import {GET_LOCATION_TYPE} from "../../locations/queries";
import {GET_ORDER_AHEAD_LOCATION_BY_ID} from "./queries";
import {Vendor} from "../../../types/vendor";
import {PaymentProcessor} from "../../../types/paymentProcessor";
import {OrderAheadLocation} from "../../../types/orderAhead";
import {useQuery} from "@tanstack/react-query";
import {GET_ORDER_AHEAD_ITEMS} from "../order-ahead-items/queries";
import {customAlphabet} from "nanoid";
import PercentageInput from "../../../components/inputs/PercentageInput";
import {CategoryManager, categoryObjectToArray, Category} from "../../menus/components/CategoryManager";
import {GET_IMAGE_LIBRARY_ITEMS_LIMITED} from "../../image-library/queries";
import {Breadcrumbs} from "../../../components/Breadcrumbs";
import {OrderAheadFormSection} from "../components/OrderAheadFormSection";
import { QRCodeSVG } from "qrcode.react";
import { menuOrderUrl, downloadQrPng } from "../../order-ahead/qrDownload";


const client = createApolloClient();
const {getState} = userStore;
const {getState: getOrganizationStore} = organizationStore;

export const orderAheadLocationEditLoader = async ({params}: LoaderFunctionArgs) => {
    const organizationId = getOrganizationStore().organizationId;
    const user = getState().user;
    const {id} = params;
    let eventWhere = {};
    let vendorWhere = {};

    if (user?.vendors) {
        vendorWhere = {
            ...vendorWhere,
            id: {
                _in: user?.vendors,
            },
        };
    }

    if (user?.events) {
        eventWhere = {
            ...eventWhere,
            id: {
                _in: user?.events,
            },
        };
    }

    if (organizationId) {
        vendorWhere = {
            ...vendorWhere,
            organization_id: {
                _eq: organizationId,
            },
        };
        eventWhere = {
            ...eventWhere,
            organization_id: {_eq: organizationId},
            is_active: {_eq: true},
        };
    }
    const {data: orderAheadLocationData} = await client.query({
        query: GET_ORDER_AHEAD_LOCATION_BY_ID,
        variables: {id},
    });

    const {data: vendorsData} = await client.query({
        query: GET_LIST_VENDORS_BY_ORGANIZATION,
        variables: {
            where: vendorWhere,
        },
    });

    const {data: eventsData} = await client.query({
        query: GET_LIST_EVENTS_BY_ORGANIZATION,
        variables: {
            where: eventWhere,
        },
    });
    const {data: locationTypes} = await client.query({
        query: GET_LOCATION_TYPE,
        variables: {
            where: organizationId
                ? {
                    organization_id: {
                        _in: [0, organizationId],
                    },
                    is_active: {
                        _eq: true,
                    },
                    reference_type: {
                        _eq: "locations_location_type",
                    },
                }
                : {},
        },
    });
    const {data: paymentProcessors} = await client.query({
        query: GET_LIST_PAYMENT_PROCESSORS_BY_ORGANIZATION,
        variables: {
            where: organizationId
                ? {
                    organization_id: {
                        _eq: organizationId,
                    },
                }
                : {},
        },
        fetchPolicy: "network-only",
    });
    const {data: imageLibraryData} = await client.query({
        query: GET_IMAGE_LIBRARY_ITEMS_LIMITED,
        variables: {
            where: eventWhere,
        },
    });

    return {
        orderAheadLocation: orderAheadLocationData.locations_order_ahead_by_pk,
        vendors: vendorsData.vendors,
        events: eventsData.events,
        locationTypes: locationTypes.dashboard_reference,
        paymentProcessors: paymentProcessors?.payment_processor_config,
        images: imageLibraryData?.image_library || [],
    };
};

export const orderAheadLocationEditAction = async ({
                                                  params,
                                                  request,
                                              }: ActionFunctionArgs) => {
    const {id} = params;
    const body = await request.formData();
    const allItemsValue = body.get("all_items");
    let allItemsIds: string[] = [];
    if (typeof allItemsValue === "string") {
        allItemsIds = JSON.parse(allItemsValue);
    }
    const allItemsIdsStringArray = allItemsIds.map((id) => id.toString());
    body.delete("all_items");
    const user = getState().user;
    const bodyData: any = Object.fromEntries(body);
    const categoryCount = parseInt(bodyData.category_count) || 6;
    const category: any = {};
    for (let i = 1; i <= categoryCount; i++) {
        category[`cat_${i}`] = bodyData[`cat_${i}`] || "";
        category[`cat_${i}_hide`] = bodyData[`cat_${i}_hide`] === "true";
        category[`cat_${i}_itemsIds`] = bodyData[`cat_${i}_items`]
            ?.split(",")
            ?.filter((item: string) => item && allItemsIdsStringArray.includes(item)) || [];
        category[`cat_${i}_image`] = bodyData[`cat_${i}_image`] || "";
    }
    Object.keys(bodyData).forEach(key => {
        if (key.startsWith("cat_") || key === "category_count") delete bodyData[key];
    });

    // Numeric columns reject an empty string ("invalid input syntax for type integer: ''"),
    // which happens when an optional number field is left blank. Coerce blanks to null and
    // everything else to a number.
    const num = (v: FormDataEntryValue | null) => {
        if (v === null || v === "") return null;
        const n = Number(v);
        return Number.isNaN(n) ? null : n;
    };

    let input = {
        name: body.get("name"),
        is_active: body.get("is_active"),
        event_id: num(body.get("event_id")),
        vendor_id: num(body.get("vendor_id")),
        location_type: body.get("location_type"),
        zone: body.get("zone"),
        description: body.get("description"),
        payment_processor_id: num(body.get("payment_processor_id")),
        dynamic_descriptor: body.get("dynamic_descriptor"),
        digital_surcharge_percentage: num(body.get("digital_surcharge_percentage")),
        url_guid: body.get("url_guid"),
        is_tips: body.get("is_tips"),
        tip_percentage_1: num(body.get("tip_percentage_1")),
        tip_percentage_2: num(body.get("tip_percentage_2")),
        tip_percentage_3: num(body.get("tip_percentage_3")),
        category: category,
        last_updated_by: user?.id,
        logo_url: body.get("logo_url"),
        background_url: body.get("background_url"),
        is_pickup: body.get("is_pickup"),
        pickup_note: body.get("pickup_note"),
    };

    try {
        await client.mutate({
            mutation: UPDATE_ORDER_AHEAD_LOCATION,
            variables: {
                input,
                where: {
                    id: {
                        _eq: id,
                    },
                },
            },
        });
        toast.success("Order Ahead Locations Updated");
        return redirect(`/order-ahead/overview`);
    } catch (error) {
        toast.error("Failed to Update Order Ahead Location: " + error);
    }
};

export const OrderAheadLocationEdit = () => {
    const {orderAheadLocation, vendors, events, paymentProcessors, locationTypes, images} = useLoaderData() as {
        orderAheadLocation: OrderAheadLocation;
        vendors: Vendor[];
        events: Event[];
        paymentProcessors: PaymentProcessor[];
        locationTypes: Categories[];
        images: { image_base64: string; display_name: string }[];
    };
    // Explicit check. The old `ItemsPermission` helper matched any value
    // containing "_ITEMS", so this gate was resolved by suffix, not by name.
    const { user } = usePermissions();
    const ItemsPermission = canManageOrderAhead(user);
    const navigate = useNavigate();
    const [selectedEvent, setSelectedEvent] = useState<number>(orderAheadLocation.event_id);
    const [selectedVendor, setSelectedVendor] = useState<number>(orderAheadLocation.vendor_id
    );
    const [eventZones, setEventZones] = useState<string[]>([]);
    const [zone, setZone] = useState(orderAheadLocation.zone);
    const [isUserModified, setIsUserModified] = useState<boolean>(Boolean(orderAheadLocation.dynamic_descriptor));
    const [slugInputValue, setSlugInputValue] = useState(orderAheadLocation.url_guid);
    const [inputValue, setInputValue] = useState<string>(orderAheadLocation.dynamic_descriptor || "");
    const [content, setContent] = useState<string>(orderAheadLocation.description);
    const [tips, setTips] = useState(orderAheadLocation.is_tips);
    const selectedEventObject = events.find(
        (event) => event.id === selectedEvent
    );

    const sortedPaymentProcessors = [...paymentProcessors]
        .filter((p) => p.is_active !== false)
        .sort((a, b) => a.payment_processor.localeCompare(b.payment_processor));
    const organizationId = getOrganizationStore().organizationId;
    const {
        data: itemsData,
        isLoading: itemsLoading,
        isFetching: itemsIsFetching,
        error: itemsError,
    } = useQuery<{
        itemsData: Item[];
    }>(
        [
            "table-data-menu-item-download",
            organizationId,
            selectedEvent,
            selectedVendor,
        ],
        async () => {
            // No is_active filter: inactive items must stay listed here so they
            // keep their category assignment (the save action strips ids missing
            // from all_items) and can be reactivated from the overview.
            let where: any = {};

            if (selectedEvent && selectedVendor) {
                if (organizationId) {
                    where = {
                        ...where,
                        event: {
                            organization_id: { _eq: organizationId },
                        },
                    };
                }
                where = {
                    ...where,
                    event_id: { _eq: selectedEvent },
                    vendor_id: { _in: selectedVendor },
                };

                const { data, errors } = await client.query({
                    query: GET_ORDER_AHEAD_ITEMS,
                    variables: {
                        where,
                        order_by: {
                            name: "asc",
                        },
                    },
                });
                return {
                    itemsData: data.items_order_ahead,
                };
            }
            return {
                itemsData: [],
            };
        }
    );
    const items = itemsData?.itemsData || [];
    const {
        data: itemsAllVendorsData,
        isLoading: itemsAllVendorsLoading,
        isFetching: itemsAllVendorsIsFetching,
        error: itemsAllVendorsError,
    } = useQuery<{
        itemsAllVendorsData: Item[];
    }>(
        [
            "table-data-menu-item-download-all-vendors",
            organizationId,
            selectedEvent,
        ],
        async () => {
            let where: any = {};

            if (selectedEvent) {
                if (organizationId) {
                    where = {
                        ...where,
                        event: {
                            organization_id: { _eq: organizationId },
                        },
                    };
                }
                where = {
                    ...where,
                    event_id: { _eq: selectedEvent },
                    vendor_id: { _eq: "0" }, // Use "0" as the vendor_id
                };

                const { data, errors } = await client.query({
                    query: GET_ORDER_AHEAD_ITEMS,
                    variables: {
                        where,
                        order_by: {
                            name: "asc",
                        },
                    },
                });
                return {
                    itemsAllVendorsData: data.items_order_ahead,
                };
            }
            return {
                itemsAllVendorsData: [],
            };
        }
    );
    const itemsAllVendors = itemsAllVendorsData?.itemsAllVendorsData || [];
    const itemIds = items
        .map((item) => item.id)
        .concat(itemsAllVendors.map((item) => item.id));
    const [categories, setCategories] = useState<Category[]>(() =>
        categoryObjectToArray(orderAheadLocation?.category)
    );
    const imageOptions = images.map((item: { image_base64: string; display_name: string }) => ({
        value: item.image_base64,
        label: item.display_name,
        display_name: item.display_name,
        image_base64: item.image_base64,
    }));
    const generateSlug = customAlphabet('abcdefghijklmnopqrstuvwxyz0123456789', 16);
    const handleClick = () => {
        setSlugInputValue(generateSlug());
    };

    useEffect(() => {
        if (selectedEvent) {
            const selectedEventObj = events.find(
                (event) => event.id === selectedEvent
            );
            const zones = selectedEventObj?.location_zones || [];
            setEventZones(zones);
        }
    }, [selectedEvent, events]);

    useEffect(() => {
        if (selectedEventObject && !isUserModified) {
            const prefix = "RPOS";
            const rawName = selectedEventObject.name.replace(/\s+/g, ""); // remove all spaces
            const maxNameLength = 22 - prefix.length;
            const namePart = rawName.slice(0, maxNameLength);
            setInputValue(`${prefix}${namePart}`);
        }
    }, [selectedEventObject]);

    const qrRef = useRef<HTMLDivElement>(null);
    const fetcher = useFetcher();
    const handleClone = () => {
        navigate(`/order-ahead-locations/${orderAheadLocation.id}/clone`);
    };
    return (
        <Form
            method="post"
            action={`/order-ahead-locations/${orderAheadLocation.id}/edit`}
            onKeyDown={(e) => {
                if (e.key === "Enter") {
                    e.preventDefault();
                }
            }}
        >
            <div className="min-h-screen bg-gray-100 p-4">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-4 py-3 mb-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <Breadcrumbs />
                        {ItemsPermission && (
                            <div className="flex items-center gap-2">
                                <TertiaryButton onClick={handleClone}>Clone Item</TertiaryButton>
                                <DeleteModal
                                    onConfirm={() => {
                                        fetcher.submit(
                                            {},
                                            {
                                                method: "delete",
                                                action: `/order-ahead-locations/${orderAheadLocation.id}/delete`,
                                            }
                                        );
                                    }}
                                >
                                    {({ setOpen }) => (
                                        <SecondaryButton onClick={() => setOpen(true)}>
                                            Delete
                                        </SecondaryButton>
                                    )}
                                </DeleteModal>
                            </div>
                        )}
                    </div>
                </div>

                <OrderAheadFormSection title="Menu Details">
                    <div className="grid grid-cols-6 gap-5">
                        <div className="col-span-5 sm:col-span-3">
                            <Input
                                label="Order Ahead Location Name"
                                name="name"
                                required
                                defaultValue={orderAheadLocation.name}
                            />
                        </div>
                        <IndeterminateCheckbox
                            label="Is Active?"
                            name="is_active"
                            defaultChecked={orderAheadLocation.is_active}
                        />
                        <div className="col-span-6">
                            <CustomSelect
                                title="Event"
                                name="event_id"
                                required
                                onChange={(e) => {
                                    setSelectedEvent(e);
                                    setIsUserModified(false);
                                }}
                                options={[
                                    ...(events?.map((e) => ({
                                        label: e.name,
                                        value: e.id,
                                    })) || []),
                                ]}
                                defaultValue={orderAheadLocation.event_id}
                            />
                        </div>
                        <div className="col-span-6">
                            <CustomSelect
                                title="Vendor"
                                name="vendor_id"
                                required
                                options={[
                                    ...vendors.map((vend) => ({
                                        value: vend.id,
                                        label: vend.name,
                                    })),
                                ]}
                                onChange={(e) => setSelectedVendor(e)}
                                defaultValue={orderAheadLocation.vendor_id}
                            />
                        </div>
                    </div>
                </OrderAheadFormSection>

                <OrderAheadFormSection title="Location Details">
                    <div className="grid grid-cols-6 gap-5">
                        <div className="col-span-6">
                            <CustomSelect
                                title="Location Type"
                                name="location_type"
                                required
                                options={[
                                    ...(locationTypes?.map((v) => ({
                                        label: v.reference_label,
                                        value: v.reference_value,
                                    })) || []),
                                ]}
                                defaultValue={orderAheadLocation.location_type}
                            />
                        </div>
                        <div className="col-span-6">
                            <CustomSelect
                                title="Zone"
                                name="zone"
                                value={zone}
                                options={[
                                    ...eventZones.map((zone) => ({
                                        value: zone,
                                        label: zone,
                                    })),
                                ]}
                                onChange={setZone}
                            />
                        </div>
                        <div className="col-span-6">
                            <TicketingItemHTML
                                content={content}
                                setContent={setContent}
                                label="Description"
                            />
                            <Input type="hidden" name="description" value={content}/>
                        </div>
                    </div>
                </OrderAheadFormSection>

                <OrderAheadFormSection title="Payment Details">
                    <div className="grid grid-cols-6 gap-5">
                        <div className="col-span-6">
                            <CustomSelect
                                name="payment_processor_id"
                                title="Payment Processor"
                                options={[
                                    {label: "PENDING", value: ""},
                                    ...sortedPaymentProcessors.map((p) => ({
                                        value: p.id,
                                        label: p.payment_processor,
                                    })),
                                ]}
                                defaultValue={orderAheadLocation.payment_processor_id}
                            />
                        </div>
                        <>
                            <div className="col-span-6">
                                <Input
                                    label="Processor Dynamic Descriptor"
                                    name="dynamic_descriptor"
                                    value={inputValue}
                                    onChange={(value) => {
                                        setInputValue(value as string);
                                        setIsUserModified(true);
                                    }}
                                    maxLength={22}
                                    sanitize={/[^a-zA-Z0-9 ]/g}
                                    toolTipShow={true}
                                    tooltipText={"Max 22 Characters"}
                                />
                            </div>
                        </>
                        <div className="col-span-6">
                            <PercentageInput
                                name="digital_surcharge_percentage"
                                label="Service Fee Value (%)"
                                defaultValue={orderAheadLocation.digital_surcharge_percentage}
                            />
                        </div>
                        <div className="col-span-5 sm:col-span-3">
                            <Input
                                label="Order Ahead Slug"
                                name="url_guid"
                                value={slugInputValue}
                                required
                                onChange={(value) => setSlugInputValue(value as string)}
                            />
                        </div>
                        <div className="block text-sm font-medium text-gray-700 col-span-6">
                            <SecondaryButton
                                type="button"
                                onClick={handleClick}
                            > Generate Slug
                            </SecondaryButton>
                            <a
                                href={`https://vendor-manager.roninpos.com/order/${slugInputValue}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="link-underline ml-2"
                            >
                                https://vendor-manager.roninpos.com/order/{slugInputValue}
                            </a>

                            {slugInputValue && (
                                <>
                                    <div
                                        ref={qrRef}
                                        className="mt-4 flex justify-start bg-white p-4 rounded border border-gray-200 w-fit"
                                    >
                                        <QRCodeSVG
                                            value={menuOrderUrl(slugInputValue)}
                                            size={200}
                                            includeMargin
                                        />
                                    </div>
                                    <div className="mt-2">
                                        <SecondaryButton
                                            type="button"
                                            onClick={() =>
                                                downloadQrPng(
                                                    qrRef,
                                                    `order-ahead-qr-${slugInputValue}.png`
                                                )
                                            }
                                        >
                                            Download QR
                                        </SecondaryButton>
                                    </div>
                                </>
                            )}

                            <br />
                            <div className="mt-2">
                                <span className="font-medium">Order Ahead Console URL</span>
                                <br />
                                <a
                                    href="https://vendor-manager.roninpos.com/"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="link-underline"
                                >
                                    https://vendor-manager.roninpos.com/
                                </a>
                            </div>
                        </div>
                        <div className="col-span-6">
                            <Input
                                label="Logo URL"
                                name="logo_url"
                                defaultValue={orderAheadLocation.logo_url}
                            />
                        </div>
                        <div className="col-span-6">
                            <Input
                                label="Background URL"
                                name="background_url"
                                defaultValue={orderAheadLocation.background_url}
                            />
                        </div>
                    </div>
                </OrderAheadFormSection>

                <OrderAheadFormSection title="Feature Configuration">
                    <div className="grid grid-cols-6 gap-5">
                        <div className="col-span-6 sm:col-span-1">
                            <IndeterminateCheckbox
                                label="Tips Accepted?"
                                name="is_tips"
                                defaultChecked={tips}
                                onChange={(checked) => setTips(checked as boolean)}
                            />
                        </div>
                        {tips && (
                            <div className="col-span-2 sm:col-span-1">
                                <Input
                                    label="Tip Percentage 1"
                                    name="tip_percentage_1"
                                    defaultValue={orderAheadLocation.tip_percentage_1}
                                    required
                                />
                            </div>
                        )}
                        {tips && (
                            <div className="col-span-2 sm:col-span-1">
                                <Input
                                    label="Tip Percentage 2"
                                    name="tip_percentage_2"
                                    defaultValue={orderAheadLocation.tip_percentage_2}
                                    required
                                />
                            </div>
                        )}
                        {tips && (
                            <div className="col-span-2 sm:col-span-1">
                                <Input
                                    label="Tip Percentage 3"
                                    name="tip_percentage_3"
                                    defaultValue={orderAheadLocation.tip_percentage_3}
                                    required
                                />
                            </div>
                        )}
                        <div className="col-span-6 sm:col-span-2">
                            <IndeterminateCheckbox
                                label="Offers Pickup?"
                                name="is_pickup"
                                defaultChecked={orderAheadLocation.is_pickup}
                            />
                        </div>
                        <div className="col-span-6 sm:col-span-4">
                            <Input
                                label="Pickup Location Note"
                                name="pickup_note"
                                placeholder="e.g. Tent 1, by the main stage"
                                defaultValue={orderAheadLocation.pickup_note ?? ""}
                            />
                        </div>
                    </div>
                </OrderAheadFormSection>

                <OrderAheadFormSection title="Menu Categories">
                    <CategoryManager
                        categories={categories}
                        setCategories={setCategories}
                        items={items}
                        itemsAllVendors={itemsAllVendors}
                        selectedEvent={selectedEvent?.toString()}
                        selectedVendor={selectedVendor?.toString()}
                        imageOptions={imageOptions}
                    />
                </OrderAheadFormSection>

                <input type="hidden" value={JSON.stringify(itemIds)} name="all_items" />

                <div className="flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={() => navigate(-1)}
                        className="px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="px-4 py-2 text-sm font-semibold rounded-lg shadow-sm bg-red-700 text-white hover:bg-red-800 transition-colors"
                    >
                        Save
                    </button>
                </div>
            </div>
        </Form>
    );
};
